/**
 * Configuration context: the admin-managed lists, loaded once after sign-in,
 * with lookup helpers so components can turn ids into names and colours.
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import type {
  ActivityType,
  ConfigBundle,
  FlagType,
  LeadSource,
  LifecycleCategory,
  LifecycleStage,
  ListItem,
  Reason,
  RewardPackage,
  SegmentTier,
  Stage,
  User,
} from "@otii/shared";

export interface Cfg {
  bundle: ConfigBundle;
  stage(id: string | null | undefined): Stage | undefined;
  /** Active sales stages in board order. */
  salesStages: Stage[];
  openStages: Stage[];
  segment(id: string | null | undefined): SegmentTier | undefined;
  segmentFor(siteCount: number): SegmentTier | undefined;
  user(id: string | null | undefined): User | undefined;
  activeUsers: User[];
  lifecycleStage(id: string | null | undefined): LifecycleStage | undefined;
  lifecycleCategory(id: string | null | undefined): LifecycleCategory | undefined;
  categoryOfStage(stageId: string | null | undefined): LifecycleCategory | undefined;
  /** Active lifecycle categories, each with its active stages, in order. */
  lifecycle: { category: LifecycleCategory; stages: LifecycleStage[] }[];
  listItem(id: string | null | undefined): ListItem | undefined;
  activityType(id: string | null | undefined): ActivityType | undefined;
  flagType(id: string | null | undefined): FlagType | undefined;
  reason(id: string | null | undefined): Reason | undefined;
  lostReasons: Reason[];
  churnReasons: Reason[];
  /** Active partner-pipeline stages in board order. */
  partnerStages: Stage[];
  rewardPackage(id: string | null | undefined): RewardPackage | undefined;
  activeRewardPackages: RewardPackage[];
  leadSource(id: string | null | undefined): LeadSource | undefined;
  /** Active lead sources, in Admin order. */
  leadSources: LeadSource[];
}

const Ctx = createContext<Cfg | null>(null);

function byId<T extends { id: string }>(xs: T[]) {
  const m = new Map(xs.map((x) => [x.id, x]));
  return (id: string | null | undefined) => (id ? m.get(id) : undefined);
}

export function buildCfg(b: ConfigBundle): Cfg {
  const stage = byId(b.stages);
  const lifecycleStage = byId(b.lifecycleStages);
  const lifecycleCategory = byId(b.lifecycleCategories);
  const salesPipeline = b.pipelines.find((p) => p.kind === "sales")?.id;
  const partnerPipeline = b.pipelines.find((p) => p.kind === "partner")?.id;
  const salesStages = b.stages.filter((s) => s.pipelineId === salesPipeline && s.active).sort((a, c) => a.sortOrder - c.sortOrder);
  const sortedSegs = b.segmentTiers.slice().sort((a, c) => a.minSites - c.minSites);
  return {
    bundle: b,
    stage,
    salesStages,
    openStages: salesStages.filter((s) => s.outcome === "open"),
    segment: byId(b.segmentTiers),
    segmentFor: (n) => sortedSegs.find((t) => n >= t.minSites && n <= (t.maxSites ?? Infinity)),
    user: byId(b.users),
    activeUsers: b.users.filter((u) => u.active),
    lifecycleStage,
    lifecycleCategory,
    categoryOfStage: (id) => lifecycleCategory(lifecycleStage(id)?.categoryId),
    lifecycle: b.lifecycleCategories
      .filter((c) => c.active)
      .sort((a, c) => a.sortOrder - c.sortOrder)
      .map((category) => ({
        category,
        stages: b.lifecycleStages.filter((s) => s.categoryId === category.id && s.active).sort((a, c) => a.sortOrder - c.sortOrder),
      })),
    listItem: byId([...b.providerTypes, ...b.tags, ...b.topics, ...b.onboardingSteps, ...b.partnerTypes, ...b.teams]),
    activityType: byId(b.activityTypes),
    flagType: byId(b.flagTypes),
    reason: byId(b.reasons),
    lostReasons: b.reasons.filter((r) => r.kind === "lost" && r.active),
    churnReasons: b.reasons.filter((r) => r.kind === "churn" && r.active),
    partnerStages: b.stages.filter((s) => s.pipelineId === partnerPipeline && s.active).sort((a, c) => a.sortOrder - c.sortOrder),
    rewardPackage: byId(b.rewardPackages),
    activeRewardPackages: b.rewardPackages.filter((r) => r.active),
    leadSource: byId(b.leadSources),
    leadSources: b.leadSources.filter((s) => s.active).sort((a, c) => a.sortOrder - c.sortOrder),
  };
}

export function ConfigProvider({ bundle, children }: { bundle: ConfigBundle; children: ReactNode }) {
  const cfg = useMemo(() => buildCfg(bundle), [bundle]);
  return <Ctx.Provider value={cfg}>{children}</Ctx.Provider>;
}

export function useConfig(): Cfg {
  const c = useContext(Ctx);
  if (!c) throw new Error("useConfig outside ConfigProvider");
  return c;
}
